import {
	extractGroupMembers,
	isUserMemberOfAssignmentGroup,
	isUserInAssignmentGroupById,
	parseGroupIdFromRef,
} from './assignmentGroupMembership';
import { parseUserIdFromRef } from './checklistEventAssignment';

export function getTodoAssignedGroup(todo: unknown): unknown {
	if (!todo || typeof todo !== 'object') return null;
	const t = todo as Record<string, unknown>;
	return t.assignedGroup ?? t.assigned_group ?? t.group ?? null;
}

/**
 * Returns true when a to-do should be visible under "To-Do - My":
 * assigned directly to the user, or assigned to a group the user belongs to.
 */
export function isTodoAssignedToCurrentUser(
	todo: unknown,
	currentUserId: number | null,
	userGroupIds?: ReadonlySet<number>,
	currentSecurityRoleId?: number | null
): boolean {
	if (currentUserId == null || !todo || typeof todo !== 'object') return false;

	const t = todo as Record<string, unknown>;
	const assigneeId = parseUserIdFromRef(t.user);
	if (assigneeId != null && Number(assigneeId) === Number(currentUserId)) {
		return true;
	}

	const assignedGroup = getTodoAssignedGroup(todo);
	if (assignedGroup == null) return false;

	const groupId = parseGroupIdFromRef(assignedGroup);
	if (isUserInAssignmentGroupById(groupId, userGroupIds)) {
		return true;
	}

	if (typeof assignedGroup === 'object') {
		return isUserMemberOfAssignmentGroup(
			assignedGroup,
			currentUserId,
			currentSecurityRoleId ?? null
		);
	}

	return false;
}

export function filterTodosForMyScope(
	todos: unknown[],
	currentUserId: number | null,
	userGroupIds?: ReadonlySet<number>,
	currentSecurityRoleId?: number | null
): unknown[] {
	return todos.filter((todo) =>
		isTodoAssignedToCurrentUser(todo, currentUserId, userGroupIds, currentSecurityRoleId)
	);
}

export function mergeTodosById(...lists: unknown[][]): unknown[] {
	const seen = new Set<number>();
	const merged: unknown[] = [];

	for (const list of lists) {
		for (const todo of list) {
			if (!todo || typeof todo !== 'object' || !('id' in todo)) continue;
			const id = Number((todo as { id: unknown }).id);
			if (!Number.isFinite(id) || seen.has(id)) continue;
			seen.add(id);
			merged.push(todo);
		}
	}

	return merged;
}

/**
 * Resolves which outstanding to-dos to show on dashboards:
 * - To-Do - All: all outstanding_todos plus the user's my_todos (direct + group assignment)
 * - To-Do - My only: scoped my_todos
 */
export function resolveOutstandingTodosForDisplay(options: {
	canViewToDoAll: boolean;
	canViewToDoMy: boolean;
	myTodos: unknown[];
	outstandingTodos: unknown[];
	currentUserId: number | null;
	userGroupIds?: ReadonlySet<number>;
	currentSecurityRoleId?: number | null;
}): unknown[] {
	const myScoped = filterTodosForMyScope(
		options.myTodos,
		options.currentUserId,
		options.userGroupIds,
		options.currentSecurityRoleId,
	);

	if (options.canViewToDoAll) {
		return mergeTodosById(options.outstandingTodos, myScoped);
	}

	if (options.canViewToDoMy) {
		return myScoped;
	}

	return [];
}

export { extractGroupMembers };
